import { useCallback, useEffect, useState } from 'react';
import {
  Database, RefreshCw, Search, Trash2, Download, Clock3, Pencil, X,
  Save, ShieldAlert, HardDrive, KeyRound, Activity, Users, Zap, TrendingUp, AlertTriangle, BarChart3,
} from 'lucide-react';
import {
  clearRedisKeys, deleteRedisKey, exportRedisKeys, fetchRedisKeyDetail,
  fetchRedisKeys, fetchRedisOverview, persistRedisKey, updateRedisKey,
  fetchPerformanceMetrics,
  type RedisKeyDetail, type RedisKeyListItem, type RedisOverviewResponse, type PerformanceMetrics,
} from '@/api/monitoring';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}G`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtBytes(bytes: number): string {
  if (bytes >= 1 << 30) return `${(bytes / (1 << 30)).toFixed(1)} GB`;
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${(bytes / (1 << 10)).toFixed(1)} KB`;
  return `${bytes} B`;
}
function fmtDuration(seconds: number): string {
  if (seconds <= 0) return '0 sn';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}g ${hours}sa`;
  if (hours > 0) return `${hours}sa ${minutes}dk`;
  if (minutes > 0) return `${minutes}dk`;
  return `${seconds}sn`;
}

// ── Card primitives ───────────────────────────────────────────────────────────
type Status = 'ok' | 'warn' | 'error' | 'info' | 'muted';

const STATUS_COLORS: Record<Status, { card: string; icon: string; value: string }> = {
  ok:    { card: 'border-emerald-100 bg-emerald-50/40',  icon: 'text-emerald-500', value: 'text-emerald-700' },
  warn:  { card: 'border-amber-100   bg-amber-50/40',    icon: 'text-amber-500',   value: 'text-amber-700'   },
  error: { card: 'border-rose-100    bg-rose-50/40',     icon: 'text-rose-500',    value: 'text-rose-700'    },
  info:  { card: 'border-blue-100    bg-blue-50/40',     icon: 'text-blue-500',    value: 'text-blue-700'    },
  muted: { card: 'border-gray-100    bg-gray-50',        icon: 'text-gray-400',    value: 'text-gray-700'    },
};

function MetricCard({
  icon, label, value, sub, status = 'muted', progress, action,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  status?: Status;
  progress?: number;
  action?: React.ReactNode;
}) {
  const c = STATUS_COLORS[status];
  return (
    <div className={`rounded-lg border p-2 flex flex-col gap-1 ${c.card}`}>
      <div className="flex items-center justify-between">
        <span className={`${c.icon}`}>{icon}</span>
        <div className="flex items-center gap-1">
          {action}
          {status !== 'muted' && (
            <span className={`text-[10px] font-medium px-1 py-0.5 rounded-full ${
              status === 'ok'    ? 'bg-emerald-100 text-emerald-700' :
              status === 'warn'  ? 'bg-amber-100   text-amber-700'   :
              status === 'error' ? 'bg-rose-100    text-rose-700'    :
                                   'bg-blue-100    text-blue-700'
            }`}>
              {status === 'ok' ? 'İyi' : status === 'warn' ? 'Dikkat' : status === 'error' ? 'Kritik' : 'Bilgi'}
            </span>
          )}
        </div>
      </div>
      <div>
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className={`text-sm font-bold leading-snug ${c.value}`}>{value}</p>
        {sub && <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{sub}</p>}
      </div>
      {progress != null && (
        <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              progress >= 90 ? 'bg-rose-400' : progress >= 70 ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl bg-white shadow-sm p-4 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function RedisKeyModal({
  db,
  item,
  onClose,
  onSaved,
}: {
  db: number;
  item: RedisKeyListItem;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [detail, setDetail] = useState<RedisKeyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [rawValue, setRawValue] = useState('');
  const [ttlValue, setTtlValue] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchRedisKeyDetail(db, item.key)
      .then(data => {
        if (cancelled) return;
        setDetail(data);
        setRawValue(stringifyValue(data.value));
        setTtlValue(data.ttl_seconds >= 0 ? String(data.ttl_seconds) : '');
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.data?.error ?? 'Redis key detayi alinamadi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [db, item.key]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    setError('');
    try {
      const parsedValue = parseEditorValue(detail.type, rawValue);
      await updateRedisKey(db, detail.key, {
        type: detail.type,
        ttl_seconds: ttlValue === '' ? null : Number(ttlValue),
        value: parsedValue,
      });
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Kaydetme basarisiz');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirmToast('Key silinsin mi?', item.key))) return;
    setSaving(true);
    try {
      await deleteRedisKey(db, item.key);
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Silme basarisiz');
    } finally {
      setSaving(false);
    }
  };

  const handlePersist = async () => {
    setSaving(true);
    try {
      await persistRedisKey(db, item.key);
      const refreshed = await fetchRedisKeyDetail(db, item.key);
      setDetail(refreshed);
      setTtlValue('');
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'TTL kaldirilamadi');
    } finally {
      setSaving(false);
    }
  };

  function ttlBadge(ttl: number) {
    if (ttl === -1) return 'Kalici';
    if (ttl === -2) return 'Silinmis';
    return fmtDuration(ttl);
  }

  function stringifyValue(value: unknown): string {
    if (typeof value === 'string') return value;
    return JSON.stringify(value ?? null, null, 2);
  }

  function parseEditorValue(type: string, raw: string): unknown {
    if (type === 'string') return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex flex-col bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <KeyRound size={16} className="text-amber-500" />
              <span className="font-semibold text-gray-800 truncate">{item.key}</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">DB {db} · {item.type.toUpperCase()} · TTL: {ttlBadge(item.ttl_seconds)}</div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && <div className="text-sm text-gray-400">Yukleniyor...</div>}
          {detail && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={<Database size={18} />} label="Type" value={detail.type.toUpperCase()} />
                <StatCard icon={<Clock3 size={18} />} label="TTL" value={ttlBadge(detail.ttl_seconds)} />
                <StatCard icon={<HardDrive size={18} />} label="Boyut" value={fmtBytes(detail.memory_usage)} />
                <StatCard icon={<KeyRound size={18} />} label="Anahtar" value="1" sub="Tek kayit" />
              </div>

              <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-start">
                <label className="text-sm font-medium text-gray-600 pt-2">TTL (sn)</label>
                <input
                  type="number"
                  min={0}
                  value={ttlValue}
                  onChange={e => setTtlValue(e.target.value)}
                  placeholder="Bos birak = degistirme"
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-start">
                <label className="text-sm font-medium text-gray-600 pt-2">Deger</label>
                <textarea
                  rows={16}
                  value={rawValue}
                  onChange={e => setRawValue(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {detail.type !== 'string' && (
                <div className="rounded-xl bg-amber-50 text-amber-700 px-4 py-3 text-sm">
                  String disindaki tiplerde duzenleme JSON formatinda yapiliyor. Hash için object, list/set için array, zset için
                  <span className="font-mono"> [{'{'} member, score {'}'}] </span>
                  kullan.
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t">
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> Sil
            </button>
            <button
              onClick={handlePersist}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-200 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              <ShieldAlert size={14} /> TTL Kaldir
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">Kapat</button>
            <button
              onClick={handleSave}
              disabled={saving || loading || !detail}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={14} /> Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RedisTab() {
  const [db, setDb] = useState(0);
  const [pattern, setPattern] = useState('*');
  const [searchInput, setSearchInput] = useState('*');
  const [overview, setOverview] = useState<RedisOverviewResponse | null>(null);
  const [perfData, setPerfData] = useState<PerformanceMetrics | null>(null);
  const [items, setItems] = useState<RedisKeyListItem[]>([]);
  const [cursor, setCursor] = useState('0');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [selected, setSelected] = useState<RedisKeyListItem | null>(null);

  const load = useCallback(async (opts?: { append?: boolean; nextCursor?: string; pattern?: string }) => {
    const append = opts?.append ?? false;
    const currentCursor = opts?.nextCursor ?? '0';
    const currentPattern = opts?.pattern ?? pattern;

    try {
      if (!append) setLoading(true);
      const [overviewRes, keysRes, perfRes] = await Promise.all([
        fetchRedisOverview(db),
        fetchRedisKeys(db, currentPattern, currentCursor, 100),
        fetchPerformanceMetrics(),
      ]);
      setOverview(overviewRes);
      setPerfData(perfRes);
      setCursor(keysRes.cursor);
      setItems(prev => append ? [...prev, ...keysRes.items] : keysRes.items);
      setLastRefresh(new Date());
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Redis verileri alinamadi');
    } finally {
      setLoading(false);
    }
  }, [db, pattern]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSearch = () => {
    const nextPattern = searchInput.trim() || '*';
    setPattern(nextPattern);
    setCursor('0');
    setItems([]);
    void load({ pattern: nextPattern });
  };

  const handleExport = async () => {
    setActionLoading(true);
    try {
      const data = await exportRedisKeys(db, pattern);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redis-db${db}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Export basarisiz');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClear = async () => {
    if (!(await confirmToast('Eşleşen keyler silinsin mi?', `DB ${db} · ${pattern}`))) return;
    setActionLoading(true);
    try {
      await clearRedisKeys(db, pattern);
      await load({ pattern });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Temizleme basarisiz');
    } finally {
      setActionLoading(false);
    }
  };

  function ttlBadge(ttl: number) {
    if (ttl === -1) return 'Kalici';
    if (ttl === -2) return 'Silinmis';
    return fmtDuration(ttl);
  }

  const { redis } = perfData || {};
  const redisMaxMemoryPct = redis && redis.maxmemory > 0 ? Math.round((redis.used_memory / redis.maxmemory) * 100) : null;

  return (
    <div className="flex flex-col gap-4 pb-6 pr-1">
      {redis && (
         <div className="space-y-4">
          {!redis.available ? (
            <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-700">
              Redis metrikleri alınamadı.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
              <MetricCard
                icon={<Activity size={14} />}
                label="Durum"
                value={redis.connected ? 'Bağlı' : 'Kesik'}
                status={redis.connected ? 'ok' : 'error'}
              />
              <MetricCard
                icon={<HardDrive size={14} />}
                label="Bellek"
                value={redis.used_memory_human ?? fmtBytes(redis.used_memory)}
                status={redisMaxMemoryPct != null && redisMaxMemoryPct >= 85 ? 'warn' : 'ok'}
              />
              <MetricCard
                icon={<Users size={14} />}
                label="İstemci"
                value={redis.connected_clients}
                status={redis.connected_clients > 100 ? 'warn' : 'info'}
              />
              <MetricCard
                icon={<Zap size={14} />}
                label="OPS"
                value={fmt(redis.instantaneous_ops_per_sec)}
                status="info"
              />
              <MetricCard
                icon={<BarChart3 size={14} />}
                label="Hit Rate"
                value={redis.hit_rate != null ? `%${redis.hit_rate}` : '—'}
                status={redis.hit_rate == null ? 'muted' : redis.hit_rate >= 90 ? 'ok' : 'warn'}
              />
              <MetricCard
                icon={<Database size={14} />}
                label="Anahtar"
                value={fmt(redis.dbsize || redis.db0_keys)}
                status="muted"
              />
              <MetricCard
                icon={<TrendingUp size={14} />}
                label="Peak"
                value={redis.used_memory_peak_human ?? '—'}
                status="muted"
              />
              <MetricCard
                icon={<AlertTriangle size={14} />}
                label="Evicted"
                value={fmt(redis.evicted_keys)}
                status={redis.evicted_keys > 0 ? 'warn' : 'ok'}
              />
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Database size={16} className="text-blue-500" />
              <select
                value={db}
                onChange={e => setDb(Number(e.target.value))}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              >
                {(overview?.databases ?? Array.from({ length: 16 }, (_, i) => ({ db: i, keys: 0, expires: 0 }))).map(item => (
                  <option key={item.db} value={item.db}>
                    {`db${item.db} (${item.keys} key)`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 min-w-[320px]">
              <Search size={15} className="text-gray-400" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                placeholder="Pattern ara. ornek: user:*"
                className="w-full text-sm outline-none"
              />
            </div>
            <button onClick={handleSearch} className="rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">Ara</button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {lastRefresh && <span className="text-xs text-gray-400">Son guncelleme: {lastRefresh.toLocaleTimeString('tr-TR')}</span>}
            <button onClick={() => void load()} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">
              <RefreshCw size={14} /> Yenile
            </button>
            <button onClick={handleExport} disabled={actionLoading} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
              <Download size={14} /> Export
            </button>
            <button onClick={handleClear} disabled={actionLoading} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              <Trash2 size={14} /> Clear
            </button>
          </div>
        </div>

      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden min-h-[500px]">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400 py-32">Yukleniyor...</div>
        ) : error ? (
          <LoadRetry />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] text-sm table-fixed border-collapse">
              <colgroup>
                <col className="w-[36%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[24%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Key</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">TTL</th>
                  <th className="px-4 py-3 text-left">Boyut</th>
                  <th className="px-4 py-3 text-left">Onizleme</th>
                  <th className="px-4 py-3 text-right">Islem</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.key} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3.5">
                      <div className="min-w-0">
                        <div
                          className="font-mono text-[12px] leading-5 text-gray-800 break-all"
                          title={item.key}
                        >
                          {item.key}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{item.type}</span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-gray-600 whitespace-nowrap">{ttlBadge(item.ttl_seconds)}</td>
                    <td className="px-4 py-3.5 text-xs text-gray-600 whitespace-nowrap">{fmtBytes(item.memory_usage)}</td>
                    <td className="px-4 py-3.5">
                      <div
                        className="text-xs leading-5 text-gray-500 break-words line-clamp-2"
                        title={item.preview}
                      >
                        {item.preview || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setSelected(item)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs whitespace-nowrap hover:bg-gray-100">
                          <Pencil size={12} /> Detay
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                      Bu pattern ile anahtar bulunamadi.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {cursor !== '0' && (
              <div className="p-4 border-t bg-gray-50 flex justify-center">
                <button
                  onClick={() => void load({ append: true, nextCursor: cursor })}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm hover:bg-white"
                >
                  Daha Fazla Yukle
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <RedisKeyModal
          db={db}
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => load({ pattern })}
        />
      )}
    </div>
  );
}
